import type { AutorizaceSluzbaAutorizaceDto } from '@gov-nx/api/generated/autorizace.types';
import { prop } from '@gov-nx/core/types/helpers.types';
import { Time } from '@gov-nx/core/types/time';
import { usePersistedStore } from '../constants';
import { excludeKeys } from '../excludeKeys';
import type {
	AutorizaceState,
	PendingAuthorizedChange,
	ProcessSuccessToast,
} from './store.types';

/**
 * How long a confirmed change stays worth reading back for. Past this the
 * register is not going to report it, and a view that kept picking the record
 * up would hold its section in a loading state on every visit.
 */
export const AUTHORIZED_CHANGE_BUDGET = Time['5 minutes'];

const initialState = {
	servicesList: [],
	isFinishing: false,
	isFailureSheetVisible: false,
	failedSubmissionId: undefined,
	authorizationId: undefined,
	ukonId: undefined,
	processSuccessToast: undefined,
	launchSiteHandlesCancel: false,
	pendingAuthorizedChange: undefined,
	_hasHydrated: false,
};

const useStore = <T>(selector: (state: AutorizaceState) => T) =>
	usePersistedStore(
		(set, get) => ({
			...initialState,
			servicesListReset: () => set({ servicesList: [] }),
			setServicesList: (list: AutorizaceSluzbaAutorizaceDto[]) => {
				set({
					servicesList: list,
				});
			},
			getServiceAuthorizationType: (serviceName: string) =>
				get().servicesList.find((s) => s.kodSluzby === serviceName)
					?.typAutorizace,
			setAuthorizationId: (authorizationId: number) => {
				set({ authorizationId });
			},
			getAuthorizationId: () => get().authorizationId,
			clearAuthorization: () => {
				set({
					authorizationId: undefined,
					ukonId: undefined,
					processSuccessToast: undefined,
					launchSiteHandlesCancel: false,
				});
			},
			setUkonId: (ukonId?: string) => {
				set({ ukonId });
			},
			getUkonId: () => get().ukonId,
			setProcessSuccessToast: (processSuccessToast?: ProcessSuccessToast) => {
				set({ processSuccessToast });
			},
			getProcessSuccessToast: () => get().processSuccessToast,
			setLaunchSiteHandlesCancel: (launchSiteHandlesCancel: boolean) => {
				set({ launchSiteHandlesCancel });
			},
			setIsFinishing: (isFinishing) => {
				set({ isFinishing });
			},
			setIsFailureSheetVisible: (isFailureSheetVisible: boolean) => {
				set({ isFailureSheetVisible });
			},
			setFailedSubmissionId: (failedSubmissionId?: string) => {
				set({ failedSubmissionId });
			},
			setHasHydrated: (hasHydrated: boolean) => {
				set({ _hasHydrated: hasHydrated });
			},
			// Armed by the view that submits the change, confirmed by whichever leg
			// of the authorization finishes it, cleared by the view once it has read
			// the change back. Deliberately outside clearAuthorization: the eID leg
			// clears the authorization on its way back into the app, long before the
			// waiting view is mounted again.
			setPendingAuthorizedChange: (change: PendingAuthorizedChange) => {
				set({ pendingAuthorizedChange: change });
			},
			confirmPendingAuthorizedChange: () => {
				const change = get().pendingAuthorizedChange;
				if (!change) {
					return;
				}
				set({
					pendingAuthorizedChange: {
						...change,
						isConfirmed: true,
						// The register write starts here, so the window does too — and
						// a repeated confirmation must not hand it a fresh one.
						deadline: change.deadline ?? Date.now() + AUTHORIZED_CHANGE_BUDGET,
					},
				});
			},
			clearPendingAuthorizedChange: () => {
				set({ pendingAuthorizedChange: undefined });
			},
		}),
		'authorization',
		selector,
		{
			onRehydrateStorage: (state) => () => state.setHasHydrated(true),
			partialize: (state) =>
				excludeKeys(state, [
					'_hasHydrated',
					'isFinishing',
					'isFailureSheetVisible',
					'failedSubmissionId',
				]),
		}
	);

export const useAutorizaceStore = <T extends keyof AutorizaceState>(
	property: T
): AutorizaceState[T] => useStore(prop(property));
